/**
 * Onboarding reference lookups — the `/v2/lookup/*` helpers that back the KYC/KYB form's
 * industry typeahead and address autocomplete. In the public self-serve signup they're called
 * before a partner key exists, so they go through {@link http} (no auth). Flows that already
 * hold a partner key (e.g. the /welcome onboarding) can pass `apiKey` to send the `api-key`
 * header, and the signed-in onboarding passes `getToken` to send the user's `Authorization: Bearer`
 * access token — some environments require an authenticated caller on these endpoints.
 *
 * Source of truth: src/apps/external/src/v2/payouts/payouts.api.external.yaml (tag: Lookup)
 */
import { http, type RequestContext } from './client';

/** Auth options accepted by every lookup helper: a partner key, the signed-in user's token, or neither. */
type LookupAuth = { apiKey?: string; getToken?: () => Promise<string | null> };

/**
 * Resolve the auth header for a lookup call. Prefers the signed-in user's Bearer token (dashboard
 * onboarding); falls back to the partner `api-key`; otherwise sends nothing (public self-serve).
 */
const authHeader = async ({ apiKey, getToken }: LookupAuth): Promise<Record<string, string>> => {
  if (getToken) {
    const token = await getToken();
    if (token) return { Authorization: `Bearer ${token}` };
  }
  return apiKey ? { 'api-key': apiKey } : {};
};

export type IndustrySuggestion = { naicsCode: string | number; title: string; subtitle?: string };

export type AddressParts = {
  line1?: string;
  line2?: string;
  city?: string;
  state?: string;
  postalCode?: string;
  country?: string;
};

export type AddressSuggestion = { id: string; formattedText: string; addressParts?: AddressParts };

const qs = (params: Record<string, string | number | undefined>): string => {
  const search = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== '') {
      search.set(k, String(v));
    }
  }
  const s = search.toString();
  return s ? `?${s}` : '';
};

/** GET /v2/lookup/industry-types — NAICS industry search for the industry typeahead. */
export async function lookupIndustries(
  env: RequestContext['env'],
  q: string,
  opts: { limit?: number; signal?: AbortSignal } & LookupAuth = {}
): Promise<IndustrySuggestion[]> {
  const res = await http<{ data: IndustrySuggestion[] }>(env, `/lookup/industry-types${qs({ q, limit: opts.limit })}`, {
    signal: opts.signal,
    headers: await authHeader(opts),
  });
  return res?.data ?? [];
}

/** GET /v2/lookup/address/suggestions — address autocomplete. Domestic (US) suggestions carry `addressParts` inline. */
export async function lookupAddresses(
  env: RequestContext['env'],
  q: string,
  opts: { type?: 'domestic' | 'international'; country?: string; signal?: AbortSignal } & LookupAuth = {}
): Promise<AddressSuggestion[]> {
  const res = await http<{ data: AddressSuggestion[] }>(
    env,
    `/lookup/address/suggestions${qs({ q, type: opts.type ?? 'domestic', country: opts.country })}`,
    { signal: opts.signal, headers: await authHeader(opts) }
  );
  return res?.data ?? [];
}

/** GET /v2/lookup/address/places/{id} — resolve an international suggestion id to full address parts. */
export async function lookupAddressParts(
  env: RequestContext['env'],
  id: string,
  opts: { type?: 'domestic' | 'international'; signal?: AbortSignal } & LookupAuth = {}
): Promise<{ id: string; addressParts: AddressParts }> {
  const res = await http<{ data: { id: string; addressParts: AddressParts } }>(
    env,
    `/lookup/address/places/${encodeURIComponent(id)}${qs({ type: opts.type ?? 'international' })}`,
    { signal: opts.signal, headers: await authHeader(opts) }
  );
  return res.data;
}
